/**
 * submitWorkflowRun — core workflow run submission logic.
 *
 * Extracted from flowstate-start's useSubmitWorkflowRun hook so every
 * Cortex surface can share the same submission path.
 *
 * Apps inject their resolved supabase client + user/org context.
 * The React hook wrappers in each app call this function.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import { buildPGMQMessage } from "@flowstate/mycelium-core";
import { getWorkflowById } from "@flowstate/types/workflow-registry";
import type { WorkflowRunPayload } from "@flowstate/types";

export interface SubmitWorkflowRunResult {
  taskId?: string;
  error?: string;
}

export interface SubmitWorkflowRunParams {
  supabase: SupabaseClient;
  userId: string;
  orgId: string | null;
  workflowId: string;
  inputs?: Record<string, unknown>;
  /** When true, uses a stable dedup_key for idempotent workflow runs */
  idempotent?: boolean;
}

/**
 * Submit a workflow run to the cortex_tasks PGMQ queue.
 *
 * The definition's `steps[]` travel frozen in the payload — the durable
 * workflow-runner interprets exactly this snapshot, so a live run is pinned
 * to the version it was submitted with. All four step kinds are supported:
 * `specialist_call`, `emit_event`, `human_review` (latent approval gate),
 * and `wait_for_event` (latent event wait).
 *
 * Logs to `cortex_task_log` immediately with status: "queued".
 */
export async function submitWorkflowRun({
  supabase,
  userId,
  orgId,
  workflowId,
  inputs,
  idempotent,
}: SubmitWorkflowRunParams): Promise<SubmitWorkflowRunResult> {
  const definition = getWorkflowById(workflowId);
  if (!definition) {
    return { error: `Unknown workflow: ${workflowId}` };
  }

  const dedupKey = idempotent
    ? `workflow-run:${userId}:${workflowId}`
    : `workflow-run:${userId}:${workflowId}:${Date.now()}`;

  const workflowRun: WorkflowRunPayload = {
    workflowId: definition.id,
    workflowVersion: definition.version,
    workflowName: definition.name,
    runMode: "async",
    steps: definition.steps,
    inputs,
  };

  const queueMessage = buildPGMQMessage({
    domain: "ai",
    type: "cortex_context_enriched",
    priority: "normal",
    source: "workflow-run",
    userId,
    principalId: userId,
    orgId: orgId ?? undefined,
    deduplicationKey: dedupKey,
    payload: {
      messages: [{ role: "user", content: `Run workflow: ${definition.name}` }],
      cortexContext: {
        backgroundJob: true,
        workflowRun,
      },
      sessionId: `wf-${dedupKey}`,
    },
  });

  // pgmq_send is a custom RPC not in the generated Supabase types
  const { error: queueError } = await supabase.rpc("pgmq_send", {
    p_queue_name: "cortex_tasks",
    p_message: queueMessage,
  });

  if (queueError) {
    return { error: `Queue error: ${queueError.message}` };
  }

  // Log to cortex_task_log immediately (status: queued)
  try {
    const { data: logData } = await supabase
      .from("cortex_task_log")
      .insert({
        trigger_event_domain: queueMessage.domain,
        trigger_event_type: "cortex_context_enriched",
        user_id: userId,
        org_id: orgId ?? null,
        intent: `workflow_run (${definition.id})`,
        context: {
          prompt: `Run workflow: ${definition.name}`,
          workflowRun,
          backgroundJob: true,
          submittedAt: new Date().toISOString(),
        },
        status: "queued",
        dedup_key: dedupKey,
      })
      .select("id")
      .single();

    return { taskId: logData?.id };
  } catch {
    // Job is already queued — non-fatal
    return {};
  }
}
